import {getTestProps} from '@benchling/js-test-utils';
import {partition} from '@benchling/js-utils';
import {Box, createStyles, Flex, Stack, Text} from '@mantine/core';
import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ExternalFileUpload from 'external-files/ExternalFileUpload';
import {insertBlockItemOnEnterKey} from 'notebook/mediocre/base-plugin/insertBlockItemOnEnterKey';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {PAPER_SIZE_INFO, PaperSize, PRINT_PAGE_INFO} from 'notebook/mediocre/print/PrintConstants';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import * as NoteLinkedFileDAO from 'notebook/notes/attachments/NoteLinkedFileDAO';
import * as NoteLinkedFileUtil from 'notebook/notes/attachments/NoteLinkedFileUtil';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import RenameNoteLinkedObjectModal from 'notebook/notes/core-ui/RenameNoteLinkedObjectModal';
import {NotePartGridNoteLinkedGroupAdapter} from 'notebook/notes/groups/note-part-grid/NotePartGridNoteLinkedGroupAdapter';
import {NoteLinkedGroupItemProps, NoteLinkedGroupKind} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import groupKindToIcon from 'notebook/notes/groups/util/groupKindToIcon';
import NoteConstants, {CidIdMap, NOTE_PADDING} from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import UploadDropdownButton from 'notebook/notes/previews/redirect-oriented-google-drive-integration/UploadDropdownButton';
import {generateNoteLinkedObjectCollapseId} from 'notebook/notes/util/generateNLCollapseId';
import {useNotebookItemCollapse} from 'notebook/tables/collapse/useNotebookItemCollapse';
import {isNoteLinkedGroup} from 'notebook/TypePredicates';
import {
  FileAddMethod,
  trackAddedFilesToNotePartGrid,
  trackBatchUploadDuration,
  trackRemovedFileFromGrid,
  trackReorderedFilesInGrid,
} from 'notebook/util/analytics/NotePartGridAnalyticsUtils';
import {
  FromServerNoteLinkedFile,
  NoteLinkedFile,
  NotePart,
  UploadSource,
} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import DnDCanvas from 'ui-library/drag-and-drop/DnDCanvas';
import {DnDDragHandleProps} from 'ui-library/drag-and-drop/DnDDragHandle';
import {DND_BLOCK_SPACING} from 'ui-library/drag-and-drop/DnDStyleConstants';
import {HoverPosition} from 'ui-library/drag-and-drop/hooks/useDnDCanvasDragState';
import moveSourceAdjacentToTarget from 'ui-library/drag-and-drop/move-item-handlers/moveSourceAdjacentToTarget';
import useMeasuredElement from 'ui-library/hooks/useMeasuredElement';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ModalAnchor from 'ui-library/ModalAnchor';
import {assertNever, ElementClipboardEvent} from 'util/Types';
import uniqueId from 'util/uniqueId';

import DraggableItem from './DraggableItem';
import {GridMovementDirection} from './KeyboardNavigationConstants';
import {
  GRID_CONTAINED_IMAGE_PADDING,
  GridPlaceholderNotePart,
  isNotePartGrid,
  MIN_ITEM_WIDTH,
  NotePartGrid as NotePartGridType,
} from './NotePartGridGroupConstants';
import NotePartGridHeader from './NotePartGridHeader';
import {computeRowsForFlowLayout, DraggableFlowItem} from './util/computeRowsForFlowLayout';
import {capImagesForGrid} from './util/gridImageLimitUtils';
import {
  createStandaloneFilePlaceholdersFromFiles,
  makeGridPlaceholderNotePart,
  type StandaloneFilePlaceholderDeps,
} from './util/gridPlaceholderUtils';
import {
  EXTERNAL_IMAGE_GRID_PASTE_MESSAGE,
  getImagesForImageGridPaste,
  INCOMPATIBLE_IMAGE_GRID_PASTE_MESSAGE,
} from './util/imageGridClipboardUtils';
import {GRID_IMAGE_ACCEPT, isGridInsertableImageType} from './util/imageTypeUtils';

export const DEFAULT_CONTAINER_WIDTH = 700;

function measureContainerWidth(element: HTMLDivElement, entry: ResizeObserverEntry | null): number {
  return Math.floor(entry?.contentRect.width ?? element.getBoundingClientRect().width);
}

/**
 * Maximum height for an image and its caption together in PDF export, derived from US Letter page
 * dimensions. mediocre.less subtracts each item's rendered caption height (measured by
 * usePrintCaptionHeight) from this to get the image's max-height, but never shrinks the image below
 * MIN_EXPORT_IMAGE_HEIGHT_PX; a caption too tall to fit beside that minimum lets the caption split across pages.
 *
 * Assumes the first row shares its page with a one-line grid title; a longer grid title can push the
 * item onto its own page. Assumes the Aphrodite styles on DnDCanvas, DraggableItem, and the preview
 * header apply; the export can drop them, which only frees up more space, so this stays safe for both
 * print preview and export. Spacing below the last row (SelectableNotePart bottom padding, wrapper
 * bottom border) is excluded because it can fall onto the next page.
 *
 * Calculation:
 *   Page height (11in × 96ppi)                                  = 1056px
 * − Top + bottom page margins (2 × ~38px)                      =   76px
 * − Export page header (entry ID bar + rule)                    =   50px
 * − Export page footer (confidentiality + page num)             =   50px
 * − SelectableNotePart top padding (NOTE_PADDING)               =   10px
 * − Bordered grid wrapper top border                            =    1px
 * − Grid title (12px top padding + 20px line + 4px margin)      =   36px
 * − Export grid container top padding                           =   16px
 * (The next two are not applied in export, only in print preview since they are Aphrodite styles, but adding
   for safety since this cap applies to print preview too)
 * − DnDCanvas top padding (DND_BLOCK_SPACING)                   =   12px
 * − DraggableItem wrapper (28px top + 4px bottom + 2px border)  =   34px
 * (Aphrodite in print preview reserves 32 px of space for filename label, whereas export reserves only 16 -- take
 * the larger of the two)
 * − Filename label above the image (fixed-height toolbar row)   =   32px
 *                                                               ────────
 *   Available for the image and its caption                     ≈  739px
 */
export const MIN_EXPORT_IMAGE_HEIGHT_PX = 200;

// Caption height assumed when usePrintCaptionHeight can't measure the caption (rendering without a
// layout engine): 4px margin + 2px border + 4 lines × 12px font × 1.4 line height.
export const FALLBACK_EXPORT_CAPTION_LINES = 4;
export const FALLBACK_EXPORT_CAPTION_HEIGHT_PX = Math.ceil(4 + 2 + FALLBACK_EXPORT_CAPTION_LINES * 12 * 1.4);

export const EXPORT_PAGE_HEADER_OR_FOOTER_PX = 50;

const NOTE_PART_GRID_TOP_BORDER_PX = 1;
export const GRID_TITLE_HEIGHT_PX = 36;
const EXPORT_GRID_CONTAINER_TOP_PADDING_PX = 16;
const DRAGGABLE_ITEM_WRAPPER_PADDING_PX = 34;
export const FILENAME_LABEL_HEIGHT_PX = 32;

// Export page header + footer + top/bottom margins
export const EXPORT_PAGE_CHROME_PX =
  2 * EXPORT_PAGE_HEADER_OR_FOOTER_PX +
  (PRINT_PAGE_INFO.margins.top + PRINT_PAGE_INFO.margins.bottom) * PRINT_PAGE_INFO.ppi;

// The chrome/image title/grid title in the NotePartGrid -- everything other than the image and its caption
export const NOTE_PART_GRID_CHROME_AND_TITLES =
  NOTE_PADDING +
  NOTE_PART_GRID_TOP_BORDER_PX +
  GRID_TITLE_HEIGHT_PX +
  EXPORT_GRID_CONTAINER_TOP_PADDING_PX +
  DND_BLOCK_SPACING +
  DRAGGABLE_ITEM_WRAPPER_PADDING_PX +
  FILENAME_LABEL_HEIGHT_PX;

// Total height of the page
export const TOTAL_PAGE_HEIGHT_PX = PAPER_SIZE_INFO[PaperSize.US_LETTER].height * PRINT_PAGE_INFO.ppi;

// Available height for the image and its caption
export const PAGE_CONTENT_HEIGHT_PX =
  TOTAL_PAGE_HEIGHT_PX - EXPORT_PAGE_CHROME_PX - NOTE_PART_GRID_CHROME_AND_TITLES;

const notePartGridAdapter = new NotePartGridNoteLinkedGroupAdapter(NoteLinkedGroupKind.NOTE_PART_GRID);

function hasExternalFiles(dataTransferTypes: DataTransfer['types']): boolean {
  return Array.from(dataTransferTypes).includes('Files');
}

// TODO(BNCH-147263): Add webp to the allowlist once preview generation support is implemented.
// TODO(BNCH-147272): Create a Helpdesk article for supported image types and link to it here.

/**
 * Resolve the server-side database ID for a contained grid item.
 *
 * After a note is saved, the server assigns permanent IDs to note parts.
 * The editor's CID→ID map (`editorProps.getIdFromCid`) reflects these
 * assignments and is the most up-to-date source. We fall back to the
 * note part's `item_id`, which may be populated from a previous save but
 * can be stale for newly created items. Returns `null` for items that
 * have never been saved.
 */
function resolveContainedItemId(notePart: NotePart, editorProps: NoteEditorProps): string | null {
  const nlfCid = (notePart.item as NoteLinkedFile | null)?.cid;
  const dbId = nlfCid ? editorProps.getIdFromCid(nlfCid) : notePart.item_id;
  return dbId != null ? String(dbId) : null;
}

function getItemKey(item: DraggableFlowItem): string {
  return item.notePart.cid;
}

/**
 * Notify the user that non-grid-insertable files were placed as standalone
 * attachments below the image grid rather than inside it.
 */
function warnNonGridFilesPlacedBelow(nonGridFiles: Array<File>): void {
  if (nonGridFiles.length === 1) {
    notifications.warn(
      `${nonGridFiles[0].name} was added below the image grid — this file type can't be viewed side-by-side.`
    );
  } else if (nonGridFiles.length === 2) {
    const names = nonGridFiles.map((f) => f.name).join(' and ');
    notifications.warn(
      `${names} were added below the image grid — these file types can't be viewed side-by-side.`
    );
  } else {
    notifications.warn(
      'Some files were added below the image grid — only supported image types can be viewed side-by-side.'
    );
  }
}

interface GridDeferredToWire {
  taskId: string;
  deferred: JQueryPromise<FromServerNoteLinkedFile>;
  fileName: string;
}

/**
 * Build the note parts that atomically replace a grid note part via a single
 * replaceItems call. Handles all combinations of grid-insertable and
 * non-grid-insertable files on both empty and populated grids:
 *
 *  - Grid images present → grid is updated with new placeholders (appended to
 *    any existing contained note parts).
 *  - No grid images, populated grid → grid is preserved as-is.
 *  - No grid images, empty grid → grid is removed.
 *  - When the grid survives, a text separator is inserted between it and the
 *    standalone files.
 *
 * A single replaceItems call is required because the editor's state management
 * can't compose two independent mutations in the same synchronous tick.
 */
function buildGridReplacementItems(
  gridInsertable: Array<File>,
  nonGridInsertable: Array<File>,
  currentGridNotePart: NotePart,
  fileId: string,
  createFilePlaceholderForTask: StandaloneFilePlaceholderDeps['createFilePlaceholderForTask'],
  createDefaultableItem: (type: string) => NotePart,
  uploadSource?: UploadSource
): {items: Array<NotePart>; deferredsToWire: Array<GridDeferredToWire>} {
  const items: Array<NotePart> = [];
  const deferredsToWire: Array<GridDeferredToWire> = [];
  const currentGroup = currentGridNotePart.item as NotePartGridType;
  const gridIsPopulated = currentGroup.containedNoteParts.length > 0;

  const cappedGridFiles = capImagesForGrid(gridInsertable, currentGroup.containedNoteParts.length);

  if (cappedGridFiles.length > 0) {
    const tasks = ExternalFileUpload.createTaskAndUploadFiles(fileId, cappedGridFiles);
    const placeholderNoteParts = tasks.map(({taskId}: {taskId: string}) =>
      makeGridPlaceholderNotePart(taskId)
    );

    items.push({
      ...currentGridNotePart,
      item: {
        ...currentGroup,
        containedNoteParts: [...currentGroup.containedNoteParts, ...placeholderNoteParts],
      },
    });

    tasks.forEach(
      (
        {
          taskId,
          fileUploadDeferred,
        }: {taskId: string; fileUploadDeferred: JQueryPromise<FromServerNoteLinkedFile>},
        i: number
      ) => {
        deferredsToWire.push({taskId, deferred: fileUploadDeferred, fileName: cappedGridFiles[i].name});
      }
    );
  } else if (gridIsPopulated) {
    items.push(currentGridNotePart);
  }

  if (items.length > 0) {
    items.push(createDefaultableItem('text'));
  }

  const standalonePlaceholders = createStandaloneFilePlaceholdersFromFiles(
    nonGridInsertable,
    {fileId, createFilePlaceholderForTask},
    uploadSource
  );
  items.push(...standalonePlaceholders);

  items.push(createDefaultableItem('text'));

  return {items, deferredsToWire};
}

export interface NotePartGridProps {
  notePartCid: string;
  group: NotePartGridType;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  itemProps?: NoteLinkedGroupItemProps;
}

interface NotePartGridUploadButtonProps {
  onLocalUploadClick: () => void;
  onGoogleDriveFilesReady: (files: Array<File>) => void;
  fileId: string;
  isEmptyState: boolean;
}

/**
 * Single entry point for rendering the NotePartGrid upload control so the
 * Google Drive picker feature-flag gate lives in one place. When `isEmptyState`
 * is true, renders a button centered in the empty grid;
 * otherwise renders a button in the title bar.
 */
function NotePartGridUploadButton({
  onLocalUploadClick,
  onGoogleDriveFilesReady,
  fileId,
  isEmptyState,
}: NotePartGridUploadButtonProps): JSX.Element {
  if (BenchlingConfig.TFI_ENABLE_GDRIVE) {
    return (
      <UploadDropdownButton
        onLocalUploadClick={onLocalUploadClick}
        onGoogleDriveFilesReady={onGoogleDriveFilesReady}
        fileId={fileId}
        multiselect={true}
        disabled={false}
        buttonStyle={ButtonStyle.SECONDARY}
        // In the header variant the button sits in a flex-centered title bar,
        // so the default `u-tiny-spacing-bottom` margin would push it upward
        // off-center against the top border.
        buttonClassName={isEmptyState ? undefined : ''}
        // Match the height of other NoteLinkedGroup header action buttons
        // (e.g. the refresh button in MixturePrep, which is MINI / 24px).
        size={isEmptyState ? undefined : ButtonSize.MINI}
      />
    );
  }
  return (
    <Button
      onClick={onLocalUploadClick}
      buttonStyle={ButtonStyle.SECONDARY}
      size={isEmptyState ? ButtonSize.MEDIUM : ButtonSize.MINI}
      text={isEmptyState ? 'Upload Files' : 'Upload'}
    />
  );
}

enum RelativePositionToNotePartGrid {
  Before, // Represents the notepart before the NotePartGrid
  Itself, // Represents the NotePartGrid itself
  After, // Represents the notepart after the NotePartGrid
}

/**
 * NotePartGrid renders a drag-and-drop grid of DraggableItems within a DnDCanvas.
 * Each item in the grid corresponds to a containedNotePart (e.g. an image attachment)
 * within a NoteLinkedGroup of kind NOTE_PART_GRID.
 */
export default function NotePartGrid({
  notePartCid,
  group,
  editor,
  editorProps,
  itemProps,
}: NotePartGridProps): JSX.Element {
  const selectNotePartInEntryRelativeToNotePartGrid = useCallback(
    (relativePosition: RelativePositionToNotePartGrid): boolean => {
      const notePartGridIndex = editor.getIndex(notePartCid);
      if (notePartGridIndex !== -1) {
        let desiredNotePartIndex: number;
        switch (relativePosition) {
          case RelativePositionToNotePartGrid.Itself:
            desiredNotePartIndex = notePartGridIndex;
            break;
          case RelativePositionToNotePartGrid.Before:
            // If NotePartGrid is at top of entry, we can't go "before", so select NotePartGrid itself
            desiredNotePartIndex = notePartGridIndex === 0 ? notePartGridIndex : notePartGridIndex - 1;
            break;
          case RelativePositionToNotePartGrid.After:
            // If NotePartGrid is at bottom of entry, can't go "after", so select NotePartGrid itself
            desiredNotePartIndex =
              notePartGridIndex === editor.props.note.noteParts.length - 1
                ? notePartGridIndex
                : notePartGridIndex + 1;
            break;
          default:
            throw new Error(`Unhandled relative position: ${assertNever(relativePosition)}`);
        }
        const desiredNotePart = editor.props.note.noteParts[desiredNotePartIndex];
        const selection = editor.selectionAtItem(desiredNotePart);
        if (selection != null) {
          editorProps.requestSelection(selection);
          return true;
        } else {
          return false;
        }
      }
      return false;
    },
    [editor, editorProps, notePartCid]
  );
  const [selectedCidInGrid, setSelectedCidInGrid] = useState<string | null>(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);

  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const prevContainedNotePartsRef = useRef(group.containedNoteParts);
  const dragElementsRef = useRef(new Set<EventTarget>());

  // Refs for stable callback access — callbacks read from these instead of
  // closing over `group`/`rows`/navigation maps, keeping their dependency
  // arrays minimal and their identities stable across reorders and resizes.
  const groupRef = useRef(group);
  groupRef.current = group;
  const rowsRef = useRef<Array<Array<DraggableFlowItem>>>([]);

  const {isPrintPreviewView, isExportView} = EditorPropertiesContext.useContext();
  const [measuredContainerWidth, setMeasuredContainerWidth] = useState(
    () => (editor.state as {editorWidth?: number}).editorWidth || DEFAULT_CONTAINER_WIDTH
  );
  const observedGridWrapperRef = useMeasuredElement(measureContainerWidth, (width) => {
    if (width > 0) {
      setMeasuredContainerWidth(width);
    }
  });

  // Set the container width to be DEFAULT_CONTAINER_WIDTH (700px) when in in-app print preview, so the grid's
  // width is less than the printable width of roughly 750px. If the grid is larger than, it will either be
  // clipped or scaled down during print.
  // Outside print preview, use the rendered wrapper width for row packing.
  const containerWidth = isPrintPreviewView ? DEFAULT_CONTAINER_WIDTH : measuredContainerWidth;
  const {classes} = useStyles({isPrintPreviewView: Boolean(isPrintPreviewView)});

  // DnDCanvas adds paddingLeft of DND_BLOCK_SPACING/2, and each DnDBlock adds DND_BLOCK_SPACING/2
  // on each side (border + padding + margin). Subtracting DND_BLOCK_SPACING * 2 accounts for the
  // canvas left padding plus one block's full chrome plus a right-edge buffer.
  // GRID_CONTAINED_IMAGE_PADDING accounts for the DraggableItem wrapper's own padding + border.
  const availableRowWidth = containerWidth - DND_BLOCK_SPACING * 2 - GRID_CONTAINED_IMAGE_PADDING;

  const maxItemWidth = Math.max(availableRowWidth, MIN_ITEM_WIDTH);

  const isEmpty = group.containedNoteParts.length === 0;
  const isProtectedAgainstEdit = editor.isProtectedAgainstEdit();
  const canRename = !isEmpty && !editorProps.locked && !editorProps.viewer && !isProtectedAgainstEdit;
  const groupDisplayName = notePartGridAdapter.getDisplayName(group);

  // Editing a caption clears both the editor's block selection and the grid's item selection, since
  // either would otherwise move focus to a focus-bearing editable and away from the caption.
  const handleBeginCaptionEdit = useCallback((): void => {
    editor.clearMultiSelect();
    setSelectedCidInGrid(null);
  }, [editor]);

  // Clear selection if the selected item no longer exists (e.g. after undo/redo or failed upload removal)
  useEffect(() => {
    if (selectedCidInGrid != null && !group.containedNoteParts.some((p) => p.cid === selectedCidInGrid)) {
      setSelectedCidInGrid(null);
    }
  }, [group.containedNoteParts, selectedCidInGrid]);

  // If we delete an item in the notepartgrid, select the entire notepartgrid and unselect all photos within the grid.
  // If we add an item, select the added item if it's not a placeholder.
  useEffect(() => {
    const prev = prevContainedNotePartsRef.current;
    prevContainedNotePartsRef.current = group.containedNoteParts;

    if (prev.length < group.containedNoteParts.length) {
      // Item added — select it if it's a real item (not an upload placeholder)
      const prevCids = new Set(prev.map((p) => p.cid));
      const addedItem = group.containedNoteParts.find((p) => !prevCids.has(p.cid) && !p.placeholder);
      if (addedItem != null) {
        setSelectedCidInGrid(addedItem.cid);
      }
    } else if (prev.length > group.containedNoteParts.length) {
      // Item deleted. We need to distinguish between user-initiated deletions and
      // internal placeholder removals (e.g. after an upload failure).
      const currentCids = new Set(group.containedNoteParts.map((p) => p.cid));
      const removedItems = prev.filter((p) => !currentCids.has(p.cid));
      const removedOnlyPlaceholders = removedItems.length > 0 && removedItems.every((p) => p.placeholder);
      if (!removedOnlyPlaceholders) {
        // Real item deleted. Select the entire notepartgrid and unselect any specific photo inside the notepartgrid.
        const wasSelectionSuccessful = selectNotePartInEntryRelativeToNotePartGrid(
          RelativePositionToNotePartGrid.Itself
        );
        if (wasSelectionSuccessful) {
          setSelectedCidInGrid(null);
        }
      }
    }
  }, [
    group.containedNoteParts,
    editor,
    editorProps,
    notePartCid,
    selectNotePartInEntryRelativeToNotePartGrid,
  ]);

  // Clear internal selection when clicking outside the grid container.
  // GridItemWrapper.handleMouseDown stopPropagation() prevents the mediocre editor from knowing about
  // clicks within the grid, so we mirror the editor's own pattern
  // (MultiSelectListUtil.multiWindowMouseDownHandler) with a document-level listener
  // that deselects when the user clicks anywhere outside.
  useEffect(() => {
    if (selectedCidInGrid == null) {
      return;
    }

    const handleDocumentMouseDown = (e: MouseEvent): void => {
      if (gridContainerRef.current && !gridContainerRef.current.contains(e.target as Node)) {
        // Don't clear when clicking into portaled menus/dropdowns — matches the mediocre
        // editor's exclusion list so that interactions with context menus, image editor
        // popovers, etc. don't inadvertently deselect the grid item.
        const target = e.target;
        if (!(target instanceof Element)) {
          return;
        }
        if (target.closest('.dropdown-menu, .backdrop, .menu, .js-mediocre-active-edit, .attachDropdown')) {
          return;
        }
        setSelectedCidInGrid(null);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [selectedCidInGrid]);

  // Compute the 2D row layout from the flat containedNoteParts list.
  // This produces the Array<Array<DraggableFlowItem>> that DnDCanvas expects.
  // Only compute when we have items (computeRowsForFlowLayout asserts non-empty).
  //
  // Note: We need to account for the grid container having a 12px padding on each side and the 12px gap
  // between items in each row.
  //
  // previousItems is built from the prior layout so computeRowsForFlowLayout can
  // reuse DraggableFlowItem references when the underlying data hasn't changed,
  // keeping DnDBlock's useMemo (keyed on the `item` prop) stable.
  const rows = useMemo(() => {
    if (isEmpty) {
      return [[]];
    }
    const previousItems: Record<string, DraggableFlowItem> = {};
    for (const row of rowsRef.current) {
      for (const item of row) {
        previousItems[item.notePart.cid] = item;
      }
    }
    return computeRowsForFlowLayout(
      group.containedNoteParts,
      availableRowWidth,
      MIN_ITEM_WIDTH,
      maxItemWidth,
      0,
      DND_BLOCK_SPACING,
      previousItems
    );
  }, [group.containedNoteParts, availableRowWidth, maxItemWidth, isEmpty]);

  rowsRef.current = rows;

  // itemIndexToRowIndex maps the 1-dimensional item index of each item in the NPG to the index of the
  // row that the item is in.
  //
  // rowIndexToFirstItemIndexInRow maps the index of each row to the 1-dimensional item index of the
  // first item that is in that row.
  const [itemIndexToRowIndex, rowIndexToFirstItemIndexInRow] = useMemo(() => {
    const itemIndexToRowIndex: Record<number, number> = {};
    const rowIndexToFirstItemIndexInRow: Record<number, number> = {};
    let currentItemIndex = 0;
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      rowIndexToFirstItemIndexInRow[rowIndex] = currentItemIndex;
      for (let _ = 0; _ < rows[rowIndex].length; _++) {
        itemIndexToRowIndex[currentItemIndex] = rowIndex;
        currentItemIndex++;
      }
    }
    return [itemIndexToRowIndex, rowIndexToFirstItemIndexInRow];
  }, [rows]);

  const itemIndexToRowIndexRef = useRef(itemIndexToRowIndex);
  itemIndexToRowIndexRef.current = itemIndexToRowIndex;
  const rowIndexToFirstItemIndexInRowRef = useRef(rowIndexToFirstItemIndexInRow);
  rowIndexToFirstItemIndexInRowRef.current = rowIndexToFirstItemIndexInRow;

  // Determine maxColumnCount from the computed layout (max items in any single row).
  const maxColumnCount = useMemo(() => Math.max(1, ...rows.map((row) => row.length)), [rows]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Persist an updated group back to the editor via itemUpdateHandler.
   */
  const updateGroup = useCallback(
    (updatedGroup: NotePartGridType): void => {
      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex !== -1) {
        editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});
      }
    },
    [editor, notePartCid]
  );

  const {isCollapsed, onToggleCollapsed} = useNotebookItemCollapse(
    generateNoteLinkedObjectCollapseId(editorProps.fileId, group.provenanceUuid)
  );

  // Print / read-only viewer should always render expanded so all images are visible.
  const isExpanded = !isCollapsed || Boolean(isPrintPreviewView);

  // ── Upload ──────────────────────────────────────────────────────────────────

  const handleUploadClick = useCallback((): void => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Update a placeholder note part in place with the actual uploaded NoteLinkedFile.
   * Preserving the placeholder's identity (cid) is critical: when autosave has
   * already persisted the placeholder with `placeholder: true`, the server sees
   * an update from placeholder→real attachment, which fires the attach audit event
   * via `NotePartPatcher.parse_note_linked_object` (`base.placeholder` is true).
   */
  const replacePlaceholder = useCallback(
    (taskId: string, serverNoteLinkedFile: FromServerNoteLinkedFile, uploadSource?: UploadSource): void => {
      if (editor.props.locked) {
        return;
      }

      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }
      const currentGroup = editor.props.note.noteParts[notePartIndex].item as NotePartGridType;

      const noteLinkedFileCid = uniqueId('note-linked-object-');
      const cidIdMap: {[cid: string]: string | number} = {[noteLinkedFileCid]: serverNoteLinkedFile.id};
      editor.props.registerObjects(cidIdMap);

      const {id: _serverId, ...serverFields} = serverNoteLinkedFile;
      const noteLinkedFileItem: NoteLinkedFile = {...serverFields, cid: noteLinkedFileCid, uploadSource};

      const newContainedNoteParts = currentGroup.containedNoteParts.map((part: NotePart) =>
        part.taskId === taskId
          ? {
              ...part,
              item_id: serverNoteLinkedFile.id,
              item: noteLinkedFileItem,
              placeholder: false,
            }
          : part
      );

      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: newContainedNoteParts,
      };
      editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});
    },
    [editor, notePartCid]
  );

  /**
   * Remove a placeholder note part on upload failure because an empty placeholder is not useful in the grid.
   */
  const removePlaceholder = useCallback(
    (taskId: string): void => {
      if (editor.props.locked) {
        return;
      }
      // Read latest state from editor — this fires asynchronously from upload deferreds.
      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }
      const currentGroup = editor.props.note.noteParts[notePartIndex].item as NotePartGridType;

      const newContainedNoteParts = currentGroup.containedNoteParts.filter(
        (part: NotePart) => part.taskId !== taskId
      );

      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: newContainedNoteParts,
      };
      editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});
    },
    [editor, notePartCid]
  );

  const wireUploadDeferred = useCallback(
    (
      taskId: string,
      deferred: JQueryPromise<FromServerNoteLinkedFile>,
      fileName?: string,
      uploadSource?: UploadSource
    ): void => {
      deferred
        .done((serverFile: FromServerNoteLinkedFile | undefined) => {
          if (serverFile != null) {
            replacePlaceholder(taskId, serverFile, uploadSource);
          } else {
            removePlaceholder(taskId);
            notifications.error(`Failed to upload image${fileName ? `: ${fileName}` : ''}`);
          }
        })
        .fail(() => {
          removePlaceholder(taskId);
          notifications.error(`Failed to upload image${fileName ? `: ${fileName}` : ''}`);
        });
    },
    [replacePlaceholder, removePlaceholder]
  );

  const uploadFilesIntoGrid = useCallback(
    (files: Array<File>, uploadSource?: UploadSource): void => {
      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }

      const startTime = performance.now();
      const fileId = editor.props.fileId;
      const taskIdAndDeferreds = ExternalFileUpload.createTaskAndUploadFiles(fileId, files);

      trackBatchUploadDuration(
        taskIdAndDeferreds.map((t) => t.fileUploadDeferred),
        startTime,
        fileId
      );

      const placeholderNoteParts = taskIdAndDeferreds.map(({taskId}: {taskId: string}) =>
        makeGridPlaceholderNotePart(taskId)
      );

      const currentGroup = editor.props.note.noteParts[notePartIndex].item as NotePartGridType;

      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: [...currentGroup.containedNoteParts, ...placeholderNoteParts],
      };
      editor.itemUpdateHandler(notePartIndex, {item: updatedGroup});

      // TODO(BNCH-145487): Replace _.defer with redux/store-based state management to avoid race
      // conditions when multiple files are uploaded in quick succession (same issue as NoteLinkedFile).
      _.defer(() => {
        taskIdAndDeferreds.forEach(
          (
            {
              taskId,
              fileUploadDeferred,
            }: {taskId: string; fileUploadDeferred: JQueryPromise<FromServerNoteLinkedFile>},
            index: number
          ) => {
            wireUploadDeferred(taskId, fileUploadDeferred, files[index].name, uploadSource);
          }
        );
      });
    },
    [editor, notePartCid, wireUploadDeferred]
  );

  /**
   * Unified entry point for processing incoming files from either the file
   * input or drag-and-drop.
   *
   * Grid-insertable images are added to the grid (with capping). Non-grid-
   * insertable files are inserted as standalone NoteLinkedFile note parts
   * below the grid. When both kinds are present, a single atomic replaceItems
   * call is used because the editor can't compose independent mutations in the
   * same synchronous tick.
   */
  const processIncomingFiles = useCallback(
    (
      allFiles: Array<File>,
      fileAddMethod: FileAddMethod = FileAddMethod.UploadButton,
      uploadSource?: UploadSource
    ): void => {
      if (allFiles.length === 0) {
        return;
      }

      const [gridInsertable, nonGridInsertable] = partition(allFiles, (f) =>
        isGridInsertableImageType(f.type)
      );
      const currentFileCount = isEmpty ? 0 : group.containedNoteParts.length;

      // A single previewable image added to an empty grid should be inserted as
      // standalone NoteLinkedFile(s) rather than a one-image grid: a lone image
      // shouldn't use the multi-image grid viewer (matching the drag-and-drop-
      // onto-note behavior). This holds even when the lone image arrives
      // alongside non-previewable files (e.g. an image + a PDF): the whole upload
      // is reclassified as standalone in its original order so no one-image grid
      // is ever created. Adding a single image to an already-populated grid still
      // stays in the grid. This applies regardless of the rollout flag: an empty
      // grid can exist even when the flag is off (e.g. all images were removed),
      // and collapsing it to standalone is correct either way.
      const collapseSingleImageToStandalone = isEmpty && gridInsertable.length === 1;
      const gridFiles = collapseSingleImageToStandalone ? [] : gridInsertable;
      const standaloneFiles = collapseSingleImageToStandalone ? allFiles : nonGridInsertable;

      // Only grid-insertable files: add them to the grid directly.
      if (standaloneFiles.length === 0) {
        if (gridFiles.length > 0) {
          const cappedFiles = capImagesForGrid(gridFiles, currentFileCount);
          if (cappedFiles.length > 0) {
            trackAddedFilesToNotePartGrid(
              editorProps.fileId,
              fileAddMethod,
              cappedFiles.length,
              0,
              currentFileCount,
              group.provenanceUuid,
              uploadSource
            );
            uploadFilesIntoGrid(cappedFiles, uploadSource);
          }
        }
        return;
      }

      // Non-grid-insertable files present (or a single image collapsing an empty
      // grid to a standalone file): use a single atomic replaceItems call to
      // handle any grid updates and standalone insertion together.
      const startTime = performance.now();
      const gridIndex = editor.getIndex(notePartCid);
      if (gridIndex === -1) {
        return;
      }

      if (editorProps.storeState != null) {
        editorProps.storeState(null);
      }

      // Warn when the grid is being kept (populated grid, or mixed files on an
      // empty grid). Skip the warning when an empty grid is being deleted and
      // replaced entirely — there is no "below the grid" to speak of.
      if (!isEmpty || gridFiles.length > 0) {
        warnNonGridFilesPlacedBelow(standaloneFiles);
      }

      const {items, deferredsToWire} = buildGridReplacementItems(
        gridFiles,
        standaloneFiles,
        editor.props.note.noteParts[gridIndex],
        editor.props.fileId,
        (task, item, source) => editor.createFilePlaceholderForTask(task, item, source),
        (type) => editor.createDefaultableItem(type),
        uploadSource
      );

      editor.replaceItems(gridIndex, 1, items, {
        onComplete: () => {
          if (deferredsToWire.length > 0) {
            trackAddedFilesToNotePartGrid(
              editorProps.fileId,
              fileAddMethod,
              deferredsToWire.length,
              standaloneFiles.length,
              currentFileCount,
              group.provenanceUuid,
              uploadSource
            );
            trackBatchUploadDuration(
              deferredsToWire.map((d) => d.deferred),
              startTime,
              editor.props.fileId
            );
          }

          // TODO(BNCH-145487): Replace _.defer with redux/store-based state management.
          if (deferredsToWire.length > 0) {
            _.defer(() => {
              deferredsToWire.forEach(({taskId, deferred, fileName}) => {
                wireUploadDeferred(taskId, deferred, fileName, uploadSource);
              });
            });
          }
        },
      });
    },
    [
      isEmpty,
      editor,
      editorProps,
      notePartCid,
      uploadFilesIntoGrid,
      wireUploadDeferred,
      group.containedNoteParts.length,
      group.provenanceUuid,
    ]
  );

  const handleGoogleDriveFiles = useCallback(
    (files: Array<File>) => {
      if (files.length > 0) {
        processIncomingFiles(files, FileAddMethod.UploadButton, UploadSource.GOOGLE_DRIVE);
      }
    },
    [processIncomingFiles]
  );

  // When the grid is empty the file input accepts all file types (accept=undefined),
  // so allFiles may include non-previewable files. processIncomingFiles routes those
  // to standalone NoteLinkedFiles. When the grid is populated, the input restricts to images only.
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const allFiles = Array.from(e.target.files || []);
      e.target.value = '';
      processIncomingFiles(allFiles, FileAddMethod.UploadButton, UploadSource.LOCAL_FILE);
    },
    [processIncomingFiles]
  );

  // ── Wire up deferreds from placeholders created via NoteEditor drag-and-drop ──
  //
  // When a user drags external files onto the note catcher, NoteEditor creates
  // placeholder NoteParts with pre-attached `_fileUploadDeferred` promises and
  // inserts them into the grid's containedNoteParts. Those placeholders arrive
  // here via props, but their deferreds still need to be connected to this
  // component's replacePlaceholder/removePlaceholder callbacks so the grid
  // updates when uploads complete.
  //
  // This effect bridges that gap: it scans for new placeholders that carry an
  // unwired `_fileUploadDeferred`, wires them up via `wireUploadDeferred`, and
  // tracks which CIDs have been processed to avoid double-wiring on re-renders.

  const processingCidsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Prune CIDs that are no longer actively uploading: either the note part
    // was removed (upload failure, undo) or the upload completed and the
    // placeholder was replaced with the real NoteLinkedFile.
    const currentPlaceholderCids = new Set(
      group.containedNoteParts.filter((p) => p.placeholder).map((p) => p.cid)
    );
    for (const cid of processingCidsRef.current) {
      if (!currentPlaceholderCids.has(cid)) {
        processingCidsRef.current.delete(cid);
      }
    }

    // Wire deferreds for any new placeholders that aren't already being processed.
    for (const notePart of group.containedNoteParts) {
      if (!notePart.placeholder) {
        continue;
      }
      const placeholder = notePart as GridPlaceholderNotePart;
      if (!placeholder._fileUploadDeferred || processingCidsRef.current.has(placeholder.cid)) {
        continue;
      }
      processingCidsRef.current.add(placeholder.cid);

      const taskId = placeholder.taskId!;
      const deferred = placeholder._fileUploadDeferred;
      const fileName = placeholder._fileName;
      _.defer(() => {
        wireUploadDeferred(taskId, deferred, fileName, UploadSource.LOCAL_FILE);
      });
    }
  }, [group.containedNoteParts, wireUploadDeferred]);

  // ── DnDCanvas: onMoveItem ─────────────────────────────────────────────────

  const handleMoveItem = useCallback(
    (source: DraggableFlowItem, target: DraggableFlowItem, hoverPosition?: HoverPosition): void => {
      const {newLayout, layoutChanged} = moveSourceAdjacentToTarget(rows, source, target, hoverPosition);
      if (!layoutChanged) {
        return;
      }
      const reorderedNoteParts = newLayout.flat().map((item) => item.notePart);

      const updatedGroup: NotePartGridType = {
        ...group,
        containedNoteParts: reorderedNoteParts,
      };
      updateGroup(updatedGroup);
      trackReorderedFilesInGrid(editorProps.fileId, reorderedNoteParts.length, group.provenanceUuid);
    },
    [rows, group, updateGroup, editorProps.fileId]
  );

  // ── DraggableItem callbacks ────────────────────────────────────────────────

  const handleSelectInGrid = useCallback((cid: string): void => {
    setSelectedCidInGrid(cid);
  }, []);

  const handleMoveSelectionInGrid = useCallback(
    (cid: string, direction: GridMovementDirection): void => {
      const currentGroup = groupRef.current;
      const index = currentGroup.containedNoteParts.findIndex((p) => p.cid === cid);
      if (index === -1) {
        return;
      }
      const currentItemIndexToRowIndex = itemIndexToRowIndexRef.current;
      const currentRowIndexToFirstItemIndex = rowIndexToFirstItemIndexInRowRef.current;
      const currentRows = rowsRef.current;

      const selectCidAtIndex = (i: number): void => {
        setSelectedCidInGrid(currentGroup.containedNoteParts[i]?.cid ?? null);
      };

      switch (direction) {
        case GridMovementDirection.Right: {
          if (index === currentGroup.containedNoteParts.length - 1) {
            const wasSelectionSuccessful = selectNotePartInEntryRelativeToNotePartGrid(
              RelativePositionToNotePartGrid.After
            );
            if (wasSelectionSuccessful) {
              setSelectedCidInGrid(null);
            }
          } else {
            selectCidAtIndex(index + 1);
          }
          break;
        }
        case GridMovementDirection.Left: {
          if (index === 0) {
            const wasSelectionSuccessful = selectNotePartInEntryRelativeToNotePartGrid(
              RelativePositionToNotePartGrid.Before
            );
            if (wasSelectionSuccessful) {
              setSelectedCidInGrid(null);
            }
          } else {
            selectCidAtIndex(index - 1);
          }
          break;
        }
        case GridMovementDirection.Up: {
          const currRowIndexInGrid = currentItemIndexToRowIndex[index];
          if (currRowIndexInGrid === 0) {
            const wasSelectionSuccessful = selectNotePartInEntryRelativeToNotePartGrid(
              RelativePositionToNotePartGrid.Before
            );
            if (wasSelectionSuccessful) {
              setSelectedCidInGrid(null);
            }
          } else {
            selectCidAtIndex(currentRowIndexToFirstItemIndex[currRowIndexInGrid - 1]);
          }
          break;
        }
        case GridMovementDirection.Down: {
          const currRowIndexInGrid = currentItemIndexToRowIndex[index];
          if (currRowIndexInGrid === currentRows.length - 1) {
            const wasSelectionSuccessful = selectNotePartInEntryRelativeToNotePartGrid(
              RelativePositionToNotePartGrid.After
            );
            if (wasSelectionSuccessful) {
              setSelectedCidInGrid(null);
            }
          } else {
            selectCidAtIndex(currentRowIndexToFirstItemIndex[currRowIndexInGrid + 1]);
          }
          break;
        }
        default:
          throw new Error(`Unhandled GridMovementDirection: ${assertNever(direction)}`);
      }
    },
    [selectNotePartInEntryRelativeToNotePartGrid]
  );

  // The grid is a single NotePart in the outer note (its individual images are not separately
  // addressable NoteParts), so Enter/Shift+Enter insert next to the whole grid rather than a specific
  // image -- same as pressing Enter on any other selected block. See `insertBlockItemOnEnterKey`.
  const handleInsertItem = useCallback(
    (e: {shiftKey: boolean}): void => {
      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }
      insertBlockItemOnEnterKey(editor.insertItemHandler, notePartIndex, e);
    },
    [editor, notePartCid]
  );

  const handleRemoveItem = useCallback(
    (cid: string): void => {
      const currentGroup = groupRef.current;
      const newContainedNoteParts = currentGroup.containedNoteParts.filter((p) => p.cid !== cid);

      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);
      trackRemovedFileFromGrid(editorProps.fileId, newContainedNoteParts.length, currentGroup.provenanceUuid);

      setSelectedCidInGrid((prev) => (prev === cid ? null : prev));
    },
    [updateGroup, editorProps.fileId]
  );

  const handleFinalizeItemWidth = useCallback(
    (cid: string, width: number): void => {
      const currentGroup = groupRef.current;
      const newContainedNoteParts = currentGroup.containedNoteParts.map((p) =>
        p.cid === cid ? {...p, itemWidth: width} : p
      );
      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);
    },
    [updateGroup]
  );

  const handleUpdateNoteLinkedObject = useCallback(
    (cid: string, updates: Partial<NoteLinkedFile>): void => {
      const currentGroup = groupRef.current;
      const newContainedNoteParts = currentGroup.containedNoteParts.map((p) =>
        p.cid === cid ? {...p, item: {...(p.item as NoteLinkedFile), ...updates}} : p
      );
      const updatedGroup: NotePartGridType = {
        ...currentGroup,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);
    },
    [updateGroup]
  );

  /**
   * Replace a contained NoteLinkedFile with a new server version, register CID
   * mappings, and persist the updated group.
   */
  const replaceContainedNoteLinkedFile = useCallback(
    (
      notePartCidToReplace: string,
      noteLinkedFile: FromServerNoteLinkedFile,
      extraFields?: Partial<NotePart>
    ): void => {
      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }
      const latestGroup = editor.props.note.noteParts[notePartIndex].item as NotePartGridType;
      const targetIndex = latestGroup.containedNoteParts.findIndex((p) => p.cid === notePartCidToReplace);
      if (targetIndex === -1) {
        return;
      }

      const currentNotePart = latestGroup.containedNoteParts[targetIndex];
      const cidIdMap: CidIdMap = {};
      const updatedNotePart = NoteLinkedFileUtil.maybeReplaceExternalFile(
        currentNotePart,
        currentNotePart,
        noteLinkedFile,
        null,
        cidIdMap
      );
      editor.props.registerObjects(cidIdMap);

      const newServerId = Object.values(cidIdMap)[0] ?? updatedNotePart.item_id;
      const newContainedNoteParts = [...latestGroup.containedNoteParts];
      newContainedNoteParts[targetIndex] = {...updatedNotePart, item_id: newServerId, ...extraFields};
      const updatedGroup: NotePartGridType = {
        ...latestGroup,
        containedNoteParts: newContainedNoteParts,
      };
      updateGroup(updatedGroup);
    },
    [editor, notePartCid, updateGroup]
  );

  // Grid-specific annotation save: resolves the contained note part by CID
  // instead of relying on NoteEditor.setImageFromS3Location (which only searches
  // top-level note parts and silently fails for contained grid cells).
  const handleSetImageFromS3Location = useCallback(
    async (
      containedCid: string,
      previewS3Location: string,
      itemWidth: number,
      name: string,
      edits?: string
    ): Promise<void> => {
      const currentNotePart = groupRef.current.containedNoteParts.find((p) => p.cid === containedCid);
      if (currentNotePart?.item_id == null) {
        return;
      }

      // transform() returns a NoteLinkedFile from the server that includes a
      // string `id`; the DAO type is slightly loose so we cast through unknown.
      const newNoteLinkedFile = (await NoteLinkedFileDAO.transform({
        uuid: currentNotePart.item_id as string,
        previewLocation: previewS3Location,
        edits,
      })) as unknown as FromServerNoteLinkedFile;

      replaceContainedNoteLinkedFile(containedCid, {...newNoteLinkedFile, name}, {itemWidth});
    },
    [replaceContainedNoteLinkedFile]
  );

  const handleSetClipboard = useCallback(
    (e: ElementClipboardEvent): void => {
      const currentSelectedCid = selectedCidInGrid;
      if (currentSelectedCid == null) {
        return;
      }
      const notePartGridIndex = editor.getIndex(notePartCid);
      if (notePartGridIndex === -1) {
        return;
      }

      const currentGroup = groupRef.current;
      const selectedItem = currentGroup.containedNoteParts.find((p) => p.cid === currentSelectedCid);
      if (selectedItem == null) {
        return;
      }

      const notePartGrid = editor.props.note.noteParts[notePartGridIndex];
      const fullItem = {
        ...notePartGrid,
        item: {
          ...notePartGrid.item,
          containedNoteParts: [selectedItem],
        },
      };
      let itemWithoutText = fullItem;
      if (editor.itemHasText?.(fullItem)) {
        itemWithoutText = editor.withUpdatedText(fullItem, {
          text: '',
          formats: formatters.defaultFormats(),
        });
      }
      if (e.clipboardData) {
        e.clipboardData.setData(
          'text/html',
          editor.processItemsForHTMLClipboard([itemWithoutText], [fullItem])
        );
        e.clipboardData.setData('text/plain', editor.processItemsForTextClipboard([itemWithoutText]));
      }
    },
    [editor, notePartCid, selectedCidInGrid]
  );

  const handlePaste = useCallback(
    async (e: ElementClipboardEvent): Promise<void> => {
      if (!e.clipboardData) {
        return;
      }
      const currentSelectedCid = selectedCidInGrid;
      if (currentSelectedCid == null) {
        return;
      }

      const {items, hasRejectedItems, fileUploadSkipped} = await editor.parseItemsFromClipboard(
        e,
        {fileId: editorProps.fileId},
        {allowFileUpload: false}
      );

      if (fileUploadSkipped) {
        notifications.warn(EXTERNAL_IMAGE_GRID_PASTE_MESSAGE);
        return;
      }

      if (hasRejectedItems) {
        return;
      }

      if (!items || items.length === 0) {
        wyciwyp.warnForNoData(false);
        return;
      }

      const itemsToPaste = getImagesForImageGridPaste(items);
      if (itemsToPaste == null) {
        notifications.warn(INCOMPATIBLE_IMAGE_GRID_PASTE_MESSAGE);
        return;
      }

      const notePartIndex = editor.getIndex(notePartCid);
      if (notePartIndex === -1) {
        return;
      }

      const currentNoteLinkedObject = editor.props.note.noteParts[notePartIndex].item;
      assert(
        currentNoteLinkedObject != null &&
          isNoteLinkedGroup(currentNoteLinkedObject) &&
          isNotePartGrid(currentNoteLinkedObject),
        'Notebook changed during paste'
      );
      const currentNotePartGrid: NotePartGridType = currentNoteLinkedObject;

      const currentSelectedIndex = currentNotePartGrid.containedNoteParts.findIndex(
        (p) => p.cid === currentSelectedCid
      );
      if (currentSelectedIndex === -1) {
        return;
      }

      // The selected image is removed before the pasted images are inserted, so it does not consume a slot.
      const remainingImageCount = currentNotePartGrid.containedNoteParts.length - 1;
      const cappedItemsToPaste = capImagesForGrid(itemsToPaste, remainingImageCount);
      if (cappedItemsToPaste.length === 0) {
        return;
      }

      if (editorProps.storeState != null) {
        editorProps.storeState(null);
      }

      const updatedContainedNoteParts = currentNotePartGrid.containedNoteParts.toSpliced(
        currentSelectedIndex,
        1,
        ...cappedItemsToPaste
      );

      const updatedNoteLinkedGroup = {
        ...currentNotePartGrid,
        containedNoteParts: updatedContainedNoteParts,
      };
      updateGroup(updatedNoteLinkedGroup);
    },
    [editor, editorProps, notePartCid, updateGroup, selectedCidInGrid]
  );

  // Persist an edited caption (text + rich-text formats) for a contained item.
  // Mirrors handleUpdateNoteLinkedObject's updateGroup pattern, but writes to the
  // note part's own `text`/`formats` fields rather than its linked object.
  const handleUpdateCaption = useCallback(
    (cid: string, text: string, formats: Format): void => {
      const currentGroup = groupRef.current;
      const containedNoteParts = currentGroup.containedNoteParts.map((p) =>
        p.cid === cid ? {...p, text, formats} : p
      );
      updateGroup({...currentGroup, containedNoteParts});
    },
    [updateGroup]
  );

  // Clicking empty space in the grid container deselects the current item.
  // GridItemWrapper.handleMouseDown calls stopPropagation(), so any click that
  // reaches this handler is guaranteed to be outside an item's content area.
  // Note: We use mouseDown instead of click here because of the complex interactions between the grid,
  // editor, and DnDCanvas which can, for example, prevent clicks from "completing" on an individual item in
  // the grid, and instead "starting" on a drag handle, but "finishing" on the grid container.
  const handleContainerMouseDown = useCallback((): void => {
    setSelectedCidInGrid(null);
  }, []);

  // ── External file drag-and-drop ─────────────────────────────────────────

  const handleDragEnter = useCallback((e: React.DragEvent): void => {
    if (hasExternalFiles(e.dataTransfer.types)) {
      dragElementsRef.current.add(e.target);
      setIsDragOver(true);
      e.stopPropagation();
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent): void => {
    if (!hasExternalFiles(e.dataTransfer.types)) {
      return;
    }
    dragElementsRef.current.delete(e.target);
    if (dragElementsRef.current.size === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleExternalDragOverCapture = useCallback((e: React.DragEvent): void => {
    if (hasExternalFiles(e.dataTransfer.types)) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleExternalDropCapture = useCallback(
    (e: React.DragEvent): void => {
      if (!hasExternalFiles(e.dataTransfer.types)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      dragElementsRef.current.clear();
      setIsDragOver(false);

      if (editorProps.locked) {
        return;
      }

      processIncomingFiles(
        Array.from(e.dataTransfer.files),
        FileAddMethod.DragAndDrop,
        UploadSource.LOCAL_FILE
      );
    },
    [editorProps.locked, processIncomingFiles]
  );

  // ── DnDCanvas: renderItem ─────────────────────────────────────────────────
  // DnDBlock memoizes the rendered element via useMemo keyed on the renderItem reference.
  // selectedCidInGrid is included as a cache-busting dep (like resolvedContainedItemIds)
  // so that DnDBlock re-invokes renderItem when selection changes. DraggableItem is wrapped
  // in React.memo, so only the 0–2 cells whose isSelectedInGrid actually flipped will
  // re-render — the rest are short-circuited by the memo comparator.

  const enableUnlockFile = editor.props.cidIdMap != null;

  const renderItem = useCallback(
    (item: DraggableFlowItem, dragHandleProps: DnDDragHandleProps, _index: number): JSX.Element => (
      <DraggableItem
        item={item}
        onBeginCaptionEdit={handleBeginCaptionEdit}
        dragHandleProps={dragHandleProps}
        editorProps={editorProps}
        maxItemWidth={maxItemWidth}
        itemId={resolveContainedItemId(item.notePart, editorProps)}
        enableUnlockFile={enableUnlockFile}
        setNoteLinkedFileFromS3Location={editor.setNoteLinkedFileFromS3Location}
        isSelectedInGrid={selectedCidInGrid === item.notePart.cid}
        onRemoveItem={handleRemoveItem}
        onFinalizeItemWidth={handleFinalizeItemWidth}
        onSelectInGrid={handleSelectInGrid}
        onMoveSelectionInGrid={handleMoveSelectionInGrid}
        onInsertItem={handleInsertItem}
        onSetClipboard={handleSetClipboard}
        onPaste={handlePaste}
        requestSelection={editorProps.requestSelection}
        isLockedWithEditWarn={editor.isLockedWithEditWarn}
        onUpdateNoteLinkedObject={handleUpdateNoteLinkedObject}
        onSetUpdatedNoteLinkedFile={replaceContainedNoteLinkedFile}
        onSetImageFromS3Location={handleSetImageFromS3Location}
        isProtectedAgainstEdit={isProtectedAgainstEdit}
        onUpdateCaption={handleUpdateCaption}
      />
    ),
    // resolvedContainedItemIds is not read directly in the callback but is included to bust
    // DnDBlock's useMemo when CID→ID mappings change after a save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      editor,
      editorProps,
      maxItemWidth,
      enableUnlockFile,
      selectedCidInGrid,
      handleRemoveItem,
      handleFinalizeItemWidth,
      handleSelectInGrid,
      handleMoveSelectionInGrid,
      handleInsertItem,
      handleSetClipboard,
      handlePaste,
      handleUpdateNoteLinkedObject,
      replaceContainedNoteLinkedFile,
      handleSetImageFromS3Location,
      handleBeginCaptionEdit,
      itemProps?.resolvedContainedItemIds,
      handleUpdateCaption,
      isProtectedAgainstEdit,
    ]
  );

  // ── DnDCanvas: row containers ──────────────────────────────────────────────

  const renderDndRowContainer = useCallback(
    ({children}: {children: JSX.Element}, rowIndex: number): JSX.Element => (
      <Flex
        key={`dnd-row-${rowIndex}`}
        wrap="wrap"
        align="flex-start"
        sx={{breakInside: 'avoid'}}
        className="notePartGrid-dndRow"
      >
        {children}
      </Flex>
    ),
    []
  );

  const itemContainers = useMemo(
    () =>
      rows.map(
        (_: unknown, i: number) => (props: {children: JSX.Element}) => renderDndRowContainer(props, i)
      ),
    [rows, renderDndRowContainer]
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  const headerUploadButton = !editorProps.locked ? (
    <div {...getTestProps({component: 'NotePartGrid', element: 'headerUpload'})}>
      <NotePartGridUploadButton
        onLocalUploadClick={handleUploadClick}
        onGoogleDriveFilesReady={handleGoogleDriveFiles}
        fileId={editorProps.fileId}
        isEmptyState={false}
      />
    </div>
  ) : null;

  return (
    <SelectableNotePart indentation={0}>
      {/*
        Only wrap populated grids in the bordered title-bar chrome. When empty,
        the dashed empty-state placeholder already conveys the grid boundary;
        rendering a solid header above it would visually compete with the
        dashed outline. This mirrors NoteLinkedFile, which only shows a name /
        action area once a file has been attached.
      */}
      <Box
        ref={isPrintPreviewView ? undefined : observedGridWrapperRef}
        pos="relative"
        w="100%"
        className={!isEmpty || isCollapsed ? classes.notePartGridWrapperBordered : undefined}
        {...getTestProps({component: 'NotePartGrid', element: 'wrapper'})}
      >
        {!isPrintPreviewView && (
          <input
            ref={fileInputRef}
            type="file"
            multiple={true}
            accept={isEmpty ? undefined : GRID_IMAGE_ACCEPT}
            aria-label="Upload files"
            onChange={handleFileChange}
            className="hidden"
            disabled={editorProps.locked}
          />
        )}
        {/* Show header when populated (like NoteLinkedFile). The `isCollapsed` guard
            ensures the header remains if a collapsed grid becomes empty (e.g. upload
            failure or collaborator deletion), so the user can still expand it.
            Hide in print/export — the header is interactive chrome, not content. */}
        {!isPrintPreviewView && (!isEmpty || isCollapsed) && (
          <NotePartGridHeader
            groupName={groupDisplayName}
            rightHeaderControls={headerUploadButton}
            isCollapsed={isCollapsed}
            onToggleCollapsed={onToggleCollapsed}
            onRename={
              canRename
                ? () => {
                    setRenameModalOpen(true);
                  }
                : undefined
            }
            viewer={editorProps.viewer}
          />
        )}
        {isPrintPreviewView && !isEmpty && (
          <div
            className="notePartGrid-printTitle"
            {...getTestProps({component: 'NotePartGrid', element: 'printTitle'})}
          >
            <BenchlingIcon
              className="notePartGrid-printTitleIcon"
              icon={groupKindToIcon(NoteLinkedGroupKind.NOTE_PART_GRID)}
            />
            {groupDisplayName}
          </div>
        )}
        <ModalAnchor open={renameModalOpen}>
          <RenameNoteLinkedObjectModal
            initialName={groupDisplayName}
            setNameAndClose={(name) => {
              if (name != null && canRename) {
                const notePartGridIndex = editor.getIndex(notePartCid);
                editor.storeStateForNoteLinkedObject(editor.atIndex(notePartGridIndex));
                editor.updateNoteLinkedObject(notePartGridIndex, {name});
              }
              setRenameModalOpen(false);
            }}
            isNameUsed={(name) => editor.props.usedTableNames().includes(name)}
            maxLength={NoteConstants.MAX_NAME_LENGTH}
            title="Rename image grid"
          />
        </ModalAnchor>
        {isExpanded && (
          <Box
            ref={gridContainerRef}
            pos="relative"
            w="100%"
            className={classNames(
              isDragOver && classes.gridContainerDragOver,
              isPrintPreviewView && 'printable-container-scaleForPrint',
              isExportView && 'notePartGrid-exportView'
            )}
            style={
              isPrintPreviewView
                ? ({
                    '--notePartGrid-page-content-height': `${PAGE_CONTENT_HEIGHT_PX}px`,
                    '--notePartGrid-min-image-height': `${MIN_EXPORT_IMAGE_HEIGHT_PX}px`,
                    '--notePartGrid-fallback-caption-height': `${FALLBACK_EXPORT_CAPTION_HEIGHT_PX}px`,
                  } as React.CSSProperties)
                : undefined
            }
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOverCapture={handleExternalDragOverCapture}
            onDropCapture={handleExternalDropCapture}
            {...getTestProps({component: 'NotePartGrid', element: 'gridContainer'})}
          >
            {isEmpty ? (
              <Flex className={classes.emptyState} align="center" justify="center">
                <Stack align="center" spacing="md" sx={{textAlign: 'center'}}>
                  <Text size="sm" className={classes.emptyStateText}>
                    Add images to view them side-by-side
                  </Text>
                  {!editorProps.locked && (
                    <div {...getTestProps({component: 'NotePartGrid', element: 'emptyStateUpload'})}>
                      <NotePartGridUploadButton
                        onLocalUploadClick={handleUploadClick}
                        onGoogleDriveFilesReady={handleGoogleDriveFiles}
                        fileId={editorProps.fileId}
                        isEmptyState={true}
                      />
                    </div>
                  )}
                </Stack>
              </Flex>
            ) : (
              <div onMouseDown={handleContainerMouseDown}>
                <DnDCanvas
                  items={rows}
                  maxColumnCount={maxColumnCount}
                  minBlockWidth={MIN_ITEM_WIDTH}
                  fitContent={true}
                  onMoveItem={handleMoveItem}
                  renderItem={renderItem}
                  itemContainers={itemContainers}
                  getItemKey={getItemKey}
                />
              </div>
            )}
          </Box>
        )}
      </Box>
    </SelectableNotePart>
  );
}

const useStyles = createStyles((theme, {isPrintPreviewView}: {isPrintPreviewView: boolean}) => {
  const grayBorderColor = theme.colors.gray[3];
  const blueAccentColor = theme.colors.blue[4];

  return {
    notePartGridWrapperBordered: {
      // Applied only when the grid has items and the header is shown. Mirrors the
      // `groupWrapper` style in NoteLinkedGroupView so a populated NotePartGrid
      // gets the same full-border chrome as mixture/stoichiometry groups.
      backgroundColor: theme.white,
      border: `1px solid ${grayBorderColor}`,
      borderRadius: theme.radius.sm,
      // Not inline-block: Chrome won't paginate inside inline-level boxes, so break-inside rules
      // on rows and items would be ignored in print and PDF export.
      display: 'block',
      maxWidth: isPrintPreviewView ? `${DEFAULT_CONTAINER_WIDTH}px` : '100%',
    },
    gridContainerDragOver: {
      backgroundColor: `${blueAccentColor}14`,
      borderRadius: theme.radius.md,
      outline: `2px dashed ${blueAccentColor}`,
      outlineOffset: '-2px',
    },
    emptyState: {
      border: `1px dashed ${grayBorderColor}`,
      borderRadius: theme.radius.md,
      minHeight: 200,
      padding: theme.spacing.xl,
    },
    emptyStateText: {
      color: blueAccentColor,
    },
  };
});
